import * as React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, ExternalLinkIcon, Plus } from 'lucide-react'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { FeatureFlags } from '@/types/feature-flags'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { ROUTES, type RouteConfig } from '@/lib/routes'

// Group routes by their group property
function groupRoutes(
  routes: RouteConfig[],
): Map<string | undefined, RouteConfig[]> {
  const groups = new Map<string | undefined, RouteConfig[]>()

  for (const route of routes) {
    const group = route.group
    if (!groups.has(group)) {
      groups.set(group, [])
    }
    groups.get(group)!.push(route)
  }

  return groups
}

interface CollapsedMenuItemProps {
  item: RouteConfig
  isActive: boolean
  filteredItems: Omit<RouteConfig, 'icon' | 'items'>[]
  pathname: string
}

function CollapsedMenuItem({
  item,
  isActive,
  filteredItems,
  pathname,
}: CollapsedMenuItemProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <SidebarMenuItem>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <SidebarMenuButton isActive={isActive} className="justify-center">
              <item.icon />
              <span className="flex-1 group-data-[collapsible=icon]:hidden">
                {item.title}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          className="w-56 p-2"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <div className="space-y-1">
            {filteredItems.map(subItem => {
              const isSubExternal = subItem.path.startsWith('http')
              const isSubActive = pathname === subItem.path

              return (
                <div key={subItem.title}>
                  {isSubExternal ? (
                    <a
                      href={subItem.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent"
                    >
                      <span className="flex-1">{subItem.title}</span>
                      <ExternalLinkIcon className="h-3 w-3 opacity-50" />
                    </a>
                  ) : (
                    <Link
                      to={subItem.path!}
                      className={`flex items-center px-2 py-1.5 text-sm rounded-md hover:bg-accent ${
                        isSubActive ? 'font-medium bg-accent' : ''
                      }`}
                    >
                      {subItem.title}
                    </Link>
                  )}
                </div>
              )
            })}
          </div>
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  )
}

export function NavMain() {
  const location = useLocation()
  const pathname = location.pathname
  const { isSuperAdmin, isPartner, currentCompanyPartner } = useAppAuth()
  const { isEnabled } = useFeatureFlag()
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  const routes = Object.values(ROUTES).filter(route => {
    // Basic filtering
    if (route.section && route.section !== 'main') return false
    if (route.adminOnly && !isSuperAdmin) return false

    // Partner filtering (partner routes live in the Partner group)
    if (route.id.startsWith('PARTNER_')) {
      // Admin-only Partner routes (like Partners management) are always visible to super admins
      // The adminOnly check above already filters non-super-admins, so we just need to
      // filter non-admin Partner routes based on partner access
      if (!route.adminOnly) {
        // Non-admin Partner routes require partner access:
        // 1. Partner users (isPartner is true)
        // 2. Super admins viewing a partner company (currentCompanyPartner is set)
        const showPartnerRoutes =
          isPartner || (isSuperAdmin && currentCompanyPartner !== null)
        if (!showPartnerRoutes) return false
      }
    }

    // Show only if required feature flag is enabled
    if (route.featureFlag && !isEnabled(route.featureFlag)) {
      return false
    }

    // Show only if the "disabled" feature flag is NOT enabled
    if (route.featureFlagDisabled && isEnabled(route.featureFlagDisabled)) {
      return false
    }

    return true
  })

  // Always show sidebar groups (Monitor, Configure, Admin) for consistent new layout
  const groupedRoutes = groupRoutes(routes)

  // Define the order of groups (undefined = ungrouped items at bottom)
  const groupOrder = ['Monitor', 'Configure', 'Partner', 'Admin', undefined]
  const sortedGroups = [...groupedRoutes.entries()].sort((a, b) => {
    const aIndex = groupOrder.indexOf(a[0])
    const bIndex = groupOrder.indexOf(b[0])
    return aIndex - bIndex
  })

  const renderMenuItem = (item: RouteConfig) => {
    const isActive = pathname.startsWith(item.path)
    const isExternal = item.path.startsWith('http')

    if (item.items?.length) {
      const filteredItems = item.items.filter(
        subItem =>
          (!subItem.adminOnly || isSuperAdmin) &&
          // Hide API keys if outbound feature flag is not enabled
          (subItem.id !== 'SETTINGS_API_KEYS' ||
            isEnabled(FeatureFlags.OUTBOUND)) &&
          // Hide items that require a feature flag that is not enabled
          (!subItem.featureFlag || isEnabled(subItem.featureFlag)),
      )

      // When collapsed, show popover on hover with sub-links
      if (isCollapsed) {
        return (
          <CollapsedMenuItem
            key={item.title}
            item={item}
            isActive={isActive}
            filteredItems={filteredItems}
            pathname={pathname}
          />
        )
      }

      // When expanded, show collapsible menu
      return (
        <SidebarMenuItem key={item.title}>
          <Collapsible defaultOpen={isActive} className="group">
            <CollapsibleTrigger asChild>
              <SidebarMenuButton isActive={isActive} tooltip={item.title}>
                <item.icon className="ml-1" />
                <span className="flex-1">{item.title}</span>
                <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {filteredItems.map(subItem => {
                  const isSubExternal = subItem.path.startsWith('http')

                  return (
                    <SidebarMenuSubItem key={subItem.title}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={pathname === subItem.path}
                      >
                        {isSubExternal ? (
                          <a
                            href={subItem.path}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="flex-1">{subItem.title}</span>
                            <ExternalLinkIcon className="h-3 w-3 opacity-50" />
                          </a>
                        ) : (
                          <Link to={subItem.path!}>
                            <span>{subItem.title}</span>
                          </Link>
                        )}
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  )
                })}
              </SidebarMenuSub>
            </CollapsibleContent>
          </Collapsible>
        </SidebarMenuItem>
      )
    }

    // For items without subitems, they can be direct links
    return (
      <SidebarMenuItem key={item.title} className="group/menuitem">
        <SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
          {isExternal ? (
            <a href={item.path} target="_blank" rel="noopener noreferrer">
              <item.icon className="ml-1" />
              <span>{item.title}</span>
            </a>
          ) : (
            <Link to={item.path!} className="flex-1">
              <item.icon className="ml-1" />
              <span>{item.title}</span>
            </Link>
          )}
        </SidebarMenuButton>
        {item.addAction && !isCollapsed && (
          <Link
            to={item.addAction}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded border bg-background shadow-sm hover:bg-accent opacity-0 group-hover/menuitem:opacity-100 transition-opacity"
            onClick={e => e.stopPropagation()}
          >
            <Plus className="h-3 w-3 text-muted-foreground" />
          </Link>
        )}
      </SidebarMenuItem>
    )
  }

  return (
    <>
      {sortedGroups.map(([groupName, groupRoutes]) => (
        <SidebarGroup key={groupName || 'ungrouped'}>
          {groupName && !isCollapsed && (
            <SidebarGroupLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 py-1.5">
              {groupName}
            </SidebarGroupLabel>
          )}
          <SidebarMenu>{groupRoutes.map(renderMenuItem)}</SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  )
}
